export interface SelfServiceReadinessOverride {
  readonly generalApiReachable: boolean;
  readonly telematikAvailable: boolean;
  readonly selectedCardReaderAvailable: boolean;
  readonly hasMedicalCard: boolean;
}

export interface SelfServiceFlowAvailability {
  readonly isConnected: boolean;
  readonly schedulerAvailable: boolean;
  readonly prescriptionAvailable: boolean;
}

/**
 * Derives kiosk flow availability from practice settings and an optional host override.
 *
 * @since 0.0.0
 * @example
 * ```ts
 * const availability = resolveSelfServiceFlowAvailability({
 *   bridgeConnected: true,
 *   schedulerEnabled: true,
 *   prescriptionEnabled: true,
 *   readinessOverride: null,
 * })
 * ```
 */
export function resolveSelfServiceFlowAvailability({
  bridgeConnected,
  schedulerEnabled,
  prescriptionEnabled,
  readinessOverride,
}: {
  readonly bridgeConnected: boolean;
  readonly schedulerEnabled: boolean;
  readonly prescriptionEnabled: boolean;
  readonly readinessOverride?: SelfServiceReadinessOverride | null;
}): SelfServiceFlowAvailability {
  const isConnected =
    readinessOverride?.generalApiReachable ?? bridgeConnected;

  if (!readinessOverride) {
    return {
      isConnected,
      schedulerAvailable: schedulerEnabled,
      prescriptionAvailable: prescriptionEnabled,
    };
  }

  const capabilityAvailable =
    readinessOverride.generalApiReachable
    && readinessOverride.telematikAvailable
    && readinessOverride.selectedCardReaderAvailable
    && readinessOverride.hasMedicalCard;

  return {
    isConnected,
    schedulerAvailable: schedulerEnabled,
    prescriptionAvailable: prescriptionEnabled && capabilityAvailable,
  };
}
